import { View } from '@play-co/timestep-core/ui';
import uiConfig from 'src/lib/ui/config';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import i18n from 'src/lib/i18n/i18n';
import { MenuActionType } from '../components/MenuActionButton';
import PopupBasic from '../components/popups/PopupBasic';
import { FooterButtonType } from '../components/Footer';
import { ShopActionType } from '../components/ShopActionButton';
import { FriendsAction } from '../components/FriendsActionButton';
import { assertNever } from 'src/replicant/utils';
import { SideButtonFeatures } from '../components/SideButton';

type CloseHandler = () => void;

// For this menu types we had translations
// If you want to add more, add translations to featureDisabled key first
export type AvailableDisabledFeatures =
  | 'duo'
  | 'duoReceivedList'
  | 'duoSentList'
  | 'gift'
  | 'care'
  | 'profile'
  | 'familyTree'
  | 'encyclopedia'
  | 'mission'
  | 'decor'
  | 'shop'
  | 'game'
  | 'friends'
  | 'invite'
  | 'energy'
  | 'gems'
  | 'visitFriend'
  | 'visitorList';

export type LockedReason =
  | 'sleeping'
  | 'visitingFriend'
  | 'pendingInvitation'
  | 'pendingResponse'
  | 'dead'
  | 'notAdult'
  | 'visitFriend';

type Feature = Extract<
  | FooterButtonType
  | MenuActionType
  | FriendsAction
  | ShopActionType
  | SideButtonFeatures,
  AvailableDisabledFeatures
>;

const popup = <const>{
  width: 556,
  height: 360,
};

const config = <const>{
  popup,
  box: {
    height: popup.height,
    image: `assets/ui/popups/dialog_panel.png`,
    scaleMethod: '9slice',
    sourceSlices: {
      horizontal: { left: 72, right: 72 },
      vertical: { top: 72, bottom: 72 },
    },
    debug: true,
  },
  title: {
    x: 40,
    y: 20,
    width: popup.width - 80,
    align: 'center',
    verticalAlign: 'center',
    size: 40,
    height: 50,
    color: '#9A7171',
    wordWrap: true,
    font: 'Body',
  },
  message: {
    x: 33,
    y: 113,
    width: popup.width - 2 * 33,
    height: 90,
    font: 'Body',
    color: '#CD9090',
    size: 29,
    wordWrap: true,
    verticalAlign: 'center',
    align: 'center',
    leading: 20,
  },
  bg: {
    backgroundColor: 'rgba(71,24,67,0.7)',
  },
  line: {
    backgroundColor: '#DBEDFB',
    height: 5,
    width: popup.width * 0.8,
    x: (popup.width - popup.width * 0.8) / 2,
    y: 78,
  },
  okButton: {
    ...uiConfig.buttons.secondary,
    width: 216,
    height: 81,
    x: popup.width / 2,
    y: 273,
    centerOnOrigin: true,
    font: 'Body',
    fontSize: 48,
    labelOffsetY: -4,
    labelOffsetX: -4,
    localeText: () => i18n('basic.ok'),
    tracking: {
      feature: 'popup',
      $subFeature: `popup-not-available-basic-ok`,
    },
  },
};

export default class PopupFeatureDisabled extends PopupBasic {
  private close: CloseHandler;
  private altTitle: LangBitmapFontTextView;
  private line: View;
  private okButton: ButtonScaleViewWithText;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      ...opts,
      ...config.popup,
      boxOpts: config.box,
      closeableWithBg: false,
      skipTitle: true,
      tracking: { feature: 'popup', $subFeature: 'popup-not-available' },
    });

    this.close = opts.close;

    this.altTitle = new LangBitmapFontTextView({
      superview: this.box,
      ...config.title,
    });

    this.line = new View({
      superview: this.box,
      ...config.line,
    });

    this.okButton = new ButtonScaleViewWithText({
      superview: this.box,
      ...config.okButton,
      onClick: async () => opts.close(),
    });

    this.bg.updateOpts(config.bg);
    this.buttonClose.removeFromSuperview();
    this.message.updateOpts(config.message);
  }

  init({ feature, reason }: { feature: Feature; reason?: LockedReason }) {
    let key: string = feature;

    if (reason) {
      switch (reason) {
        case 'sleeping':
        case 'pendingInvitation':
        case 'pendingResponse':
        case 'notAdult':
          key = `${feature}-${reason}`;
          break;
        case 'visitingFriend':
          key = 'visitingFriend';
          break;
        case 'visitFriend':
          key = 'visitFriend';
          break;
        case 'dead':
          key = 'dead';
          break;
        default:
          assertNever(reason);
      }
    }

    const data = {
      title: i18n(`featureDisabled.${key}.title`),
      message: i18n(`featureDisabled.${key}.message`),
    };

    super.init({ message: data.message });
    this.altTitle.localeText = () => data.title;
  }
}
